import {
  Box,
  Button,
  Heading,
  NeoColor,
  P2,
  P4,
  Stack,
} from "@noom/wax-component-library";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  MedCtaAction,
  trackPrescriptionWebviewCtaTapped,
} from "../../nativeAnalytics/medAnalytics";
import { TreatmentActionLinkRow } from "../TreatmentActionLinkRow";
import { TreatmentProductLockup } from "../TreatmentProductLockup";
import {
  followTreatmentActionLinkNavigation,
  resolveTreatmentActionLinkNavigation,
} from "../actionLinkNavigation";
import { useTrackYourPrescriptionWebviewViewed } from "../hooks/useTrackYourPrescriptionWebviewViewed";
import type { TreatmentOverview } from "../hooks/useTreatmentOverviews";
import { isLabTreatmentOverview } from "../treatmentOverviewGuards";
import { openDeeplink } from "../utils";

import { DetailShell } from "./DetailShell";
import { getDoseSummaryLockupProps } from "./doseSummaryLockup";
import { getSupplementDirectionsLockupProps } from "./supplementDirectionsLockup";
import { LabResultsCard } from "./LabResultsCard";
import { TreatmentMedLogCtas } from "./TreatmentMedLogCtas";
import { TreatmentStatusCard } from "./TreatmentStatusCard";
import {
  getTreatmentDetailViewModel,
  type DoseBannerMode,
} from "./treatmentDetailViewModel";

type TreatmentDetailReadyContentProps = {
  treatment: TreatmentOverview;
};

const doseBannerLabelKey = (
  mode: DoseBannerMode
): `detail.doseBanner.${Exclude<DoseBannerMode, "none">}` | undefined => {
  switch (mode) {
    case "starting":
      return "detail.doseBanner.starting";
    case "lastDose":
      return "detail.doseBanner.lastDose";
    case "lastSmartdose":
      return "detail.doseBanner.lastSmartdose";
    default:
      return undefined;
  }
};

export const TreatmentDetailReadyContent = ({
  treatment,
}: TreatmentDetailReadyContentProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation("care", { useSuspense: false });

  useTrackYourPrescriptionWebviewViewed({
    medicationName: treatment.treatmentName,
  });

  const viewModel = getTreatmentDetailViewModel(treatment);
  const doseBannerLabel = doseBannerLabelKey(viewModel.doseBannerMode);
  const doseSummary = getDoseSummaryLockupProps(treatment, t);
  const supplementDirections = getSupplementDirectionsLockupProps(treatment, t);
  const lockupContent = doseSummary ?? supplementDirections;
  const actionLinks = treatment.actionLinks ?? [];
  const hasActionLinks = actionLinks.length > 0;
  const chatWithDoctorDeeplink = treatment.chatWithDoctorDeeplink;
  const isLab = isLabTreatmentOverview(treatment);

  return (
    <DetailShell>
      <Stack
        spacing={viewModel.cardSpacing}
        width="100%"
        pt={2}
        alignItems="stretch"
      >
        <Stack spacing={0} width="100%" alignItems="stretch">
          <P2
            fontWeight="medium"
            color={NeoColor.greyscale.black}
            noOfLines={2}
          >
            {treatment.treatmentName}
          </P2>
          {doseBannerLabel && (
            <P4
              data-cy="treatment-dose-banner"
              fontWeight="normal"
              color={NeoColor.greyscale.gray4}
            >
              {t(doseBannerLabel)}
            </P4>
          )}
          {viewModel.productPresentation === "labResults" &&
            viewModel.resultsCard && (
              <LabResultsCard
                resultsCard={viewModel.resultsCard}
                medicationName={treatment.treatmentName}
                productIconUrl={treatment.productIconUrl}
              />
            )}
        </Stack>
        {viewModel.productPresentation === "lockupBand" && (
          <Stack spacing={0} width="100%" alignItems="stretch">
            <TreatmentProductLockup
              {...(lockupContent ?? {})}
              imageUrl={treatment.productIconUrl}
              imageAlt={treatment.treatmentName}
              productBackgroundColor={treatment.productBackgroundColor}
            />
            {viewModel.showMedLogCtAs && (
              <TreatmentMedLogCtas medicationName={treatment.treatmentName} />
            )}
          </Stack>
        )}
        {viewModel.statusCard && (
          <TreatmentStatusCard
            statusCard={viewModel.statusCard}
            medicationName={treatment.treatmentName}
          />
        )}
      </Stack>

      {hasActionLinks && (
        <Stack width="100%" pt={6} spacing={2} alignItems="stretch">
          {isLab && (
            <Heading
              size="xxs"
              fontWeight="normal"
              color={NeoColor.greyscale.black}
            >
              {t("detail.quickLinks")}
            </Heading>
          )}
          <Box width="100%" bg={NeoColor.greyscale.white}>
            {actionLinks.map((link) => {
              const localizedLabel = link.linkKey
                ? t(`actionLinks.${link.linkKey}`, { defaultValue: link.label })
                : link.label;
              return (
                <TreatmentActionLinkRow
                  key={`${link.linkKey ?? "link"}-${link.url}`}
                  grouped
                  link={{
                    label: localizedLabel,
                    url: link.url,
                    iconUrl: link.iconUrl,
                  }}
                  onClick={() => {
                    void trackPrescriptionWebviewCtaTapped({
                      ctaText: localizedLabel,
                      context: "your_links",
                      medicationName: treatment.treatmentName,
                      ctaAction: link.linkKey ?? undefined,
                    });
                    followTreatmentActionLinkNavigation(
                      resolveTreatmentActionLinkNavigation(
                        link.url,
                        localizedLabel
                      ),
                      navigate
                    );
                  }}
                />
              );
            })}
          </Box>
        </Stack>
      )}

      {chatWithDoctorDeeplink && (
        // Full-width pill via WAX `lg` Button (29px borderRadius = half
        // the 58px height). Outline appearance via colorScheme=secondary.
        // Matches the design's bottom CTA spec (343px wide, 56px radius —
        // 56 ≥ height/2, so visually identical to the WAX pill).
        <Box width="100%" pt={hasActionLinks ? 4 : 6} pb={6}>
          <Button
            data-cy="treatment-chat-with-doctor"
            size="lg"
            variant="solid"
            colorScheme="secondary"
            isFullWidth
            onClick={() => {
              void trackPrescriptionWebviewCtaTapped({
                ctaText: t("chatWithDoctor"),
                context: "chat_with_your_doctor",
                medicationName: treatment.treatmentName,
                ctaAction: MedCtaAction.Chat,
              });
              openDeeplink(chatWithDoctorDeeplink);
            }}
          >
            {t("chatWithDoctor")}
          </Button>
        </Box>
      )}
    </DetailShell>
  );
};
